import { apiClient } from "../client";

export interface Asset {
  id: number;
  assetTagId: string;
  assetImage?: string;
  name: string;
  description: string | null;
  brand: string | null;
  model: string | null;
  purchasePrice: number | null;
  purchasedFrom?: string | null;
  currentValue?: number | null;
  serialNumber: string | null;
  purchaseDate: string | null;
  purchaseCost: string | null;
  warrantyExpiry: string | null;
  siteId: number;
  organizationId: number;
  categoryId: number | null;
  departmentId: number | null;
  locationId: number | null;
  healthStatusId: number | null;
  complianceStatusId: number | null;
  assetStateId: number | null;
  criticalityId: number | null;
  assignedToId: number | null;
  maintainedById: number | null;
  maintenanceUpToDate: boolean;
  lastMaintenanceDate: string | null;
  nextMaintenanceDate: string | null;
  maintenanceScheduleFrequency: string | null;
  maintenanceScheduleConfig: {
    frequency: string;
    customDays?: number;
    startDate?: string;
    endDate?: string;
    neverEnding?: boolean;
    excludeWeekends?: boolean;
  } | null;
  failureProbability: number | null;
  createdBy: number;
  updatedBy: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  category?: { id: number; name: string };
  healthStatus?: { id: number; name: string; color?: string };
  complianceStatus?: { id: number; name: string };
  assetState?: { id: number; name: string };
  department?: { id: number; name: string };
  location?: { id: number; name: string };
  criticality?: { id: number; name: string };
  assignedTo?: {
    id: number;
    name: string;
    email: string;
    profilePicture?: string;
  };
  maintainedBy?: {
    id: number;
    name: string;
    email: string;
    profilePicture?: string | null;
  };
  creator: {
    id: number;
    email: string;
    googleId: string | null;
    name: string;
    profilePicture: string | null;
  };
  updater?: { id: number; name: string; email: string };
}

export interface AssetUpdate {
  id: number;
  assetId: number;
  content: string;
  createdBy: number;
  createdAt: string;
  creator: {
    id: number;
    email: string;
    googleId: string | null;
    name: string;
    profilePicture: string | null;
  };
}

export interface AssetDocument {
  id: number;
  type: "DOCUMENT" | "LINK";
  name: string;
  url: string;
  description: string | null;
  mimeType: string | null;
  fileSize: number | null;
  assetId: number;
  uploadedBy: number;
  uploader?: {
    id: number;
    email: string;
    googleId: string | null;
    name: string;
    profilePicture: string | null;
    isVerified: boolean;
    verificationCode: string;
    verificationCodeExpiry: string;
    isWelcomeCompleted: boolean;
    onboardingCompleted: boolean;
    createdAt: string;
    updatedAt: string;
  };
  createdAt: string;
}

export interface AuditLog {
  id: number;
  date: string;
  user: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
  };
  actionType: string;
  description: string;
  oldValue: string | null;
  newValue: string | null;
}

export enum IssueStatus {
  REPORTED = "REPORTED",
  ASSIGNED = "ASSIGNED",
  ON_HOLD = "ON_HOLD",
  RESOLVED = "RESOLVED",
}

export interface AssetIssue {
  id: number;
  name: string;
  description: string | null;
  images: string[] | null;
  reportedDate: string;
  status: IssueStatus;
  asset: {
    id: number;
    name: string;
    assetTagId: string;
    criticality?: { id: number; name: string };
    department?: { id: number; name: string };
    location?: { id: number; name: string };
  };
  reporter: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
  } | null;
  maintainer: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: number;
  healthStatusId?: number;
  complianceStatusId?: number;
  assetStateId?: number;
  criticalityId?: number;
  departmentId?: number;
  locationId?: number;
  assignedToId?: number;
  maintenanceUpToDate?: boolean;
}

export interface AssetIssueFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  assetId?: number;
  status?: IssueStatus;
  reportedBy?: number;
  maintainedBy?: number;
  criticalityId?: number;
  departmentId?: number;
  locationId?: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetAssetsResponse {
  message: string;
  assets: Asset[];
  pagination: PaginationMeta;
}

export interface CreateAssetData {
  name: string;
  description?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyExpiry?: string;
  categoryId?: number;
  departmentId?: number;
  locationId?: number;
  healthStatusId?: number;
  complianceStatusId?: number;
  assetStateId?: number;
  criticalityId?: number;
  assignedToId?: number;
  maintainedById?: number;
}

export interface UpdateAssetData {
  name?: string;
  description?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyExpiry?: string;
  categoryId?: number;
  departmentId?: number;
  locationId?: number;
}

export interface UploadIssueImagesResponse {
  message: string;
  files: {
    filename: string;
    originalName: string;
    path: string;
    size: number;
    mimetype: string;
  }[];
  count: number;
}

export interface UpdateMaintenanceData {
  maintenanceUpToDate: boolean;
  lastMaintenanceDate?: string;
  nextMaintenanceDate?: string;
  maintainedById?: number;
}

export interface MaintenanceScheduleData {
  frequency: string;
  customDays?: number;
  startDate?: string;
  endDate?: string;
  neverEnding?: boolean;
  excludeWeekends?: boolean;
}

export interface CreateAssetUpdateData {
  content: string;
}

export interface CreateAssetDocumentData {
  name: string;
  url: string;
  type: "DOCUMENT" | "LINK";
}

export interface CreateAssetIssueData {
  name: string;
  assetId: number;
  reportedBy: number;
  reportedDate: string;
  description?: string;
  imagePaths?: string[];
  maintainedBy?: number;
  status?: IssueStatus;
}

export interface UpdateAssetIssueData {
  name?: string;
  description?: string;
  imagePaths?: string[];
  reportedDate?: string;
  reportedBy?: number;
  maintainedBy?: number;
  status?: IssueStatus;
}

export interface GlobalAuditLog extends AuditLog {
  assetId: number;
  assetName: string;
}

export interface GlobalAssetUpdate extends AssetUpdate {
  assetName: string;
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export class AssetService {
  static async getAssets(
    organizationId: number,
    siteId: number,
    params?: PaginationParams,
  ): Promise<GetAssetsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.categoryId)
      queryParams.append("categoryId", params.categoryId.toString());
    if (params?.healthStatusId)
      queryParams.append("healthStatusId", params.healthStatusId.toString());
    if (params?.complianceStatusId)
      queryParams.append(
        "complianceStatusId",
        params.complianceStatusId.toString(),
      );
    if (params?.assetStateId)
      queryParams.append("assetStateId", params.assetStateId.toString());
    if (params?.criticalityId)
      queryParams.append("criticalityId", params.criticalityId.toString());
    if (params?.departmentId)
      queryParams.append("departmentId", params.departmentId.toString());
    if (params?.locationId)
      queryParams.append("locationId", params.locationId.toString());
    if (params?.assignedToId)
      queryParams.append("assignedToId", params.assignedToId.toString());
    if (params?.maintenanceUpToDate !== undefined)
      queryParams.append(
        "maintenanceUpToDate",
        params.maintenanceUpToDate.toString(),
      );

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/assets?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/assets`;

    return await apiClient.get<GetAssetsResponse>(url);
  }

  static async getAllAuditLogs(
    organizationId: number,
    siteId: number,
    page: number = 1,
    limit: number = 10,
    search?: string,
    actionType?: string,
  ): Promise<{
    message: string;
    logs: GlobalAuditLog[];
    pagination: PaginationMeta;
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (search) queryParams.append("search", search);
    if (actionType) queryParams.append("actionType", actionType);

    return await apiClient.get(
      `/organizations/${organizationId}/sites/${siteId}/assets/audit-logs/all?${queryParams.toString()}`,
    );
  }

  static async getAllAssetUpdates(
    organizationId: number,
    siteId: number,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ): Promise<{
    message: string;
    updates: GlobalAssetUpdate[];
    pagination: PaginationMeta;
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (search) queryParams.append("search", search);

    return await apiClient.get(
      `/organizations/${organizationId}/sites/${siteId}/assets/updates/all?${queryParams.toString()}`,
    );
  }

  static async getAssetById(
    organizationId: number,
    siteId: number,
    assetId: number,
  ): Promise<Asset> {
    return await apiClient.get<Asset>(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}`,
    );
  }

  static async createAsset(
    organizationId: number,
    siteId: number,
    assetData: CreateAssetData,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.post(
      `/organizations/${organizationId}/sites/${siteId}/assets`,
      assetData,
    );
  }

  static async updateAsset(
    organizationId: number,
    siteId: number,
    assetId: number,
    assetData: UpdateAssetData,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}`,
      assetData,
    );
  }

  static async deleteAsset(
    organizationId: number,
    siteId: number,
    assetId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}`,
    );
  }

  static async updateMaintenance(
    organizationId: number,
    siteId: number,
    assetId: number,
    maintenanceData: UpdateMaintenanceData,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/maintenance`,
      maintenanceData,
    );
  }

  static async updateMaintenanceSchedule(
    organizationId: number,
    siteId: number,
    assetId: number,
    scheduleData: MaintenanceScheduleData,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/maintenance-schedule`,
      scheduleData,
    );
  }

  static async updateFailureProbability(
    organizationId: number,
    siteId: number,
    assetId: number,
    failureProbability: number,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/failure-probability`,
      { failureProbability },
    );
  }

  static async updateHealthStatus(
    organizationId: number,
    siteId: number,
    assetId: number,
    healthStatusId: number,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/health`,
      { healthStatusId },
    );
  }

  static async updateComplianceStatus(
    organizationId: number,
    siteId: number,
    assetId: number,
    complianceStatusId: number,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/compliance-status`,
      { complianceStatusId },
    );
  }

  static async updateCriticality(
    organizationId: number,
    siteId: number,
    assetId: number,
    criticalityId: number,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/criticality`,
      { criticalityId },
    );
  }

  static async updateAssetState(
    organizationId: number,
    siteId: number,
    assetId: number,
    assetStateId: number,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/asset-state`,
      { assetStateId },
    );
  }

  static async updateAssignedTo(
    organizationId: number,
    siteId: number,
    assetId: number,
    assignedToId: number | null,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/assigned-to`,
      { assignedToId },
    );
  }

  static async updateMaintainedBy(
    organizationId: number,
    siteId: number,
    assetId: number,
    maintainedById: number | null,
  ): Promise<{ message: string; asset: Asset }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/maintained-by`,
      { maintainedById },
    );
  }

  static async createAssetUpdate(
    organizationId: number,
    siteId: number,
    assetId: number,
    updateData: CreateAssetUpdateData,
  ): Promise<{ message: string; update: AssetUpdate }> {
    return await apiClient.post(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/updates`,
      updateData,
    );
  }

  static async getAssetUpdates(
    organizationId: number,
    siteId: number,
    assetId: number,
    page: number = 1,
    limit: number = 10,
  ): Promise<{
    message: string;
    updates: AssetUpdate[];
    pagination: PaginationMeta;
  }> {
    return await apiClient.get(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/updates?page=${page}&limit=${limit}`,
    );
  }

  static async deleteAssetUpdate(
    organizationId: number,
    siteId: number,
    assetId: number,
    updateId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/updates/${updateId}`,
    );
  }

  static async createAssetDocument(
    organizationId: number,
    siteId: number,
    assetId: number,
    documentData: CreateAssetDocumentData,
  ): Promise<{ message: string; document: AssetDocument }> {
    return await apiClient.post(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/documents`,
      documentData,
    );
  }

  static async getAssetDocuments(
    organizationId: number,
    siteId: number,
    assetId: number,
    page: number = 1,
    limit: number = 10,
    search?: string, // Add search parameter
  ): Promise<{
    message: string;
    documents: AssetDocument[];
    pagination: PaginationMeta;
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (search) queryParams.append("search", search);

    return await apiClient.get(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/documents?${queryParams.toString()}`,
    );
  }

  static async deleteAssetDocument(
    organizationId: number,
    siteId: number,
    assetId: number,
    documentId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/documents/${documentId}`,
    );
  }

  static async getAssetAuditLogs(
    organizationId: number,
    siteId: number,
    assetId: number,
    page: number = 1,
    limit: number = 10,
    search?: string,
    actionType?: string,
  ): Promise<{
    message: string;
    logs: AuditLog[];
    pagination: PaginationMeta;
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (search) queryParams.append("search", search);
    if (actionType) queryParams.append("actionType", actionType);

    return await apiClient.get(
      `/organizations/${organizationId}/sites/${siteId}/assets/${assetId}/audit-logs?${queryParams.toString()}`,
    );
  }

  static async createAssetIssue(
    organizationId: number,
    siteId: number,
    issueData: CreateAssetIssueData,
  ): Promise<{ message: string; issue: AssetIssue }> {
    return await apiClient.post(
      `/organizations/${organizationId}/sites/${siteId}/assets/issues`,
      issueData,
    );
  }

  static async uploadIssueImages(
    files: File[],
  ): Promise<UploadIssueImagesResponse> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    return await apiClient.post("/upload/issue", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  static async getAssetIssues(
    organizationId: number,
    siteId: number,
    params?: AssetIssueFilterParams,
  ): Promise<{
    message: string;
    issues: AssetIssue[];
    pagination: PaginationMeta;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.assetId)
      queryParams.append("assetId", params.assetId.toString());
    if (params?.status) queryParams.append("status", params.status);
    if (params?.reportedBy)
      queryParams.append("reportedBy", params.reportedBy.toString());
    if (params?.maintainedBy)
      queryParams.append("maintainedBy", params.maintainedBy.toString());
    if (params?.criticalityId)
      queryParams.append("criticalityId", params.criticalityId.toString());
    if (params?.departmentId)
      queryParams.append("departmentId", params.departmentId.toString());
    if (params?.locationId)
      queryParams.append("locationId", params.locationId.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/assets/issues?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/assets/issues`;

    return await apiClient.get(url);
  }

  static async getAssetIssueById(
    organizationId: number,
    siteId: number,
    issueId: number,
  ): Promise<AssetIssue> {
    return await apiClient.get<AssetIssue>(
      `/organizations/${organizationId}/sites/${siteId}/assets/issues/${issueId}`,
    );
  }

  static async updateAssetIssue(
    organizationId: number,
    siteId: number,
    issueId: number,
    issueData: UpdateAssetIssueData,
  ): Promise<{ message: string; issue: AssetIssue }> {
    return await apiClient.put(
      `/organizations/${organizationId}/sites/${siteId}/assets/issues/${issueId}`,
      issueData,
    );
  }

  static async deleteAssetIssue(
    organizationId: number,
    siteId: number,
    issueId: number,
  ): Promise<{ message: string }> {
    return await apiClient.delete(
      `/organizations/${organizationId}/sites/${siteId}/assets/issues/${issueId}`,
    );
  }

  static async checkAssetTag(
    organizationId: number,
    siteId: number,
    tagId: string,
  ): Promise<{ exists: boolean }> {
    return await apiClient.get(
      `/organizations/${organizationId}/sites/${siteId}/assets/check-tag?tagId=${tagId}`,
    );
  }

  static async generateAssetTag(
    organizationId: number,
    siteId: number,
  ): Promise<{ tagId: string }> {
    return await apiClient.get(
      `/organizations/${organizationId}/sites/${siteId}/assets/generate-tag`,
    );
  }
}
